/**
 * Line-level diff for comparing two prompt bodies.
 *
 * Line granularity rather than word or character: a prompt body is a small, structured
 * document whose meaning lives in its lines (an instruction, a placeholder, a
 * delimiter), and a word-level diff of reflowed prose produces noise a reviewer has to
 * read past. Written here rather than pulled in as a dependency — the whole algorithm
 * is the function below, and a diff library would be several orders of magnitude more
 * code for the same answer on inputs this size.
 */

export enum DiffKind {
	UNCHANGED = 'unchanged',
	ADDED = 'added',
	REMOVED = 'removed',
}

export interface DiffLine {
	kind: DiffKind
	text: string
	/** 1-based line number in the OLD body; absent for an added line. */
	oldLine?: number
	/** 1-based line number in the NEW body; absent for a removed line. */
	newLine?: number
}

/**
 * Guard on the LCS table's size.
 *
 * The table is O(n×m), so two very long bodies would allocate a lot of memory on the
 * render path. Past this, the diff degrades to "everything replaced" — honest, and
 * bounded, rather than freezing the tab. A prompt body this large is well outside what
 * the editor is for.
 */
const MAX_LINES = 2000

/**
 * Longest-common-subsequence lengths, as a FLAT typed array indexed `i * width + j`.
 *
 * Flat rather than nested: a `number[][]` needs a non-null assertion on every row
 * lookup (TypeScript cannot know the row exists), and `Int32Array` both removes those
 * and allocates one buffer instead of n+1 arrays.
 */
function lcsTable(a: string[], b: string[]): { get: (i: number, j: number) => number } {
	const width = b.length + 1
	// (n+1)×(m+1) so row/column 0 is the empty-prefix base case, zero-filled by default.
	const table = new Int32Array((a.length + 1) * width)

	for (let i = a.length - 1; i >= 0; i--) {
		for (let j = b.length - 1; j >= 0; j--) {
			table[i * width + j] =
				a[i] === b[j]
					? table[(i + 1) * width + (j + 1)] + 1
					: Math.max(table[(i + 1) * width + j], table[i * width + (j + 1)])
		}
	}

	return { get: (i, j) => table[i * width + j] }
}

/** Every line of both sides marked replaced — the bail-out for oversized inputs. */
function wholesaleReplacement(oldLines: string[], newLines: string[]): DiffLine[] {
	return [
		...oldLines.map((text, i) => ({ kind: DiffKind.REMOVED, text, oldLine: i + 1 })),
		...newLines.map((text, i) => ({ kind: DiffKind.ADDED, text, newLine: i + 1 })),
	]
}

/**
 * Diff two bodies into a single ordered list of lines.
 *
 * One list rather than two columns: the caller renders a unified view, and interleaving
 * here keeps the pairing of a removal with its replacement in one place.
 */
export function diffLines(oldBody: string, newBody: string): DiffLine[] {
	const a = oldBody.split('\n')
	const b = newBody.split('\n')

	if (a.length > MAX_LINES || b.length > MAX_LINES) return wholesaleReplacement(a, b)

	const table = lcsTable(a, b)
	const out: DiffLine[] = []
	let i = 0
	let j = 0

	while (i < a.length && j < b.length) {
		const oldText = a[i] ?? ''
		const newText = b[j] ?? ''

		if (oldText === newText) {
			out.push({ kind: DiffKind.UNCHANGED, text: oldText, oldLine: i + 1, newLine: j + 1 })
			i++
			j++
			continue
		}
		// Follow the larger remaining subsequence. Ties drop the OLD line first so a
		// replacement always reads removal-then-addition.
		if (table.get(i + 1, j) >= table.get(i, j + 1)) {
			out.push({ kind: DiffKind.REMOVED, text: oldText, oldLine: i + 1 })
			i++
		} else {
			out.push({ kind: DiffKind.ADDED, text: newText, newLine: j + 1 })
			j++
		}
	}

	// Whichever side still has lines is pure removal or pure addition.
	while (i < a.length) {
		out.push({ kind: DiffKind.REMOVED, text: a[i] ?? '', oldLine: i + 1 })
		i++
	}
	while (j < b.length) {
		out.push({ kind: DiffKind.ADDED, text: b[j] ?? '', newLine: j + 1 })
		j++
	}

	return out
}

/** Whether two bodies differ at all — cheaper than diffing to find out. */
export const bodiesDiffer = (oldBody: string, newBody: string): boolean => oldBody !== newBody
